import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import CardGrid, {Card} from 'analysis-tool/ui/components/CardGrid';
import {
  AnalysisMainViewItemType,
  AnalysisMainViewItem,
  MainViewComponentProps,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert, assertAndContinue} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import useStepTypeInfos from 'pipelines/hooks/useStepTypeInfosQuery';
import {getStepTypeIconOrDefault} from 'pipelines/StepIconUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  DATA_FRAME_CREATION_METHOD_OPTIONS,
  DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
} from './DataFrameCreationConstants';
import {
  getEnabledDatasetCreationMethods,
  getSharedDataFrameCreationMethodProps,
  isReplacingTable,
  trackSelectedDataFrameCreationMethodEvent,
} from './DataFrameCreationUtils';

function getTitleText(selectedItem: AnalysisMainViewItem): string {
  if (isReplacingTable(selectedItem)) {
    return 'How do you want to replace your table?';
  }
  return 'How do you want to create your table?';
}

export default function ChooseDataFrameCreationMethodView({
  analysis,
  clientStateId,
  onCloseStepInitializationView,
  onOpenStepInitializationView,
  selectedItem,
}: MainViewComponentProps<AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD>): JSX.Element {
  const dataFrameCreationMethods = getEnabledDatasetCreationMethods(selectedItem);
  const stepTypeInfos = useStepTypeInfos();
  assert(
    dataFrameCreationMethods.length > 0,
    'Expected at least 1 data frame creation method to be enabled',
    {
      dataFrameCreationMethods,
    }
  );
  assertAndContinue(
    dataFrameCreationMethods.length > 1,
    "Expected data frame creation view to be rendered directly when there's only 1 data frame creation method enabled",
    {dataFrameCreationMethods}
  );

  return (
    <div {...getTestProps({component: 'ChooseDataFrameCreationMethodView', element: 'root'})}>
      <StepConfigurationViewTemplate
        {...getSharedDataFrameCreationMethodProps(selectedItem)}
        title={getTitleText(selectedItem)}
        clientStateId={clientStateId}
        dataFrames={[]}
        disableFinalizeButton={true}
        messages={[]}
        noPreviewPlaceholderOptions={{
          title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
          subtitle: 'Select a creation method to see a preview of your table.',
        }}
        onCancel={onCloseStepInitializationView}
        onFinalize={noop}
      >
        <CardGrid minWidth="225px">
          {dataFrameCreationMethods.map((method) => {
            const {stepType, text} = DATA_FRAME_CREATION_METHOD_OPTIONS[method];
            const icon = getStepTypeIconOrDefault(stepType, stepTypeInfos, 'table');
            // Copy initializationViewItem to avoid modifying DATA_FRAME_CREATION_METHOD_OPTIONS
            const initializationViewItem: AnalysisMainViewItem = {
              ...DATA_FRAME_CREATION_METHOD_OPTIONS[method].initializationViewItem,
            };
            if (isReplacingTable(selectedItem) && DATA_FRAME_CREATION_METHOD_OPTIONS[method].canReplace) {
              // @ts-expect-error - We have different enums for DataFrameCreationMethod and AnalysisMainViewItemType, typescript doesn't know non-replacing methods are excluded
              initializationViewItem.stepIdToReplace = selectedItem.stepIdToReplace;
            }
            return (
              <Card
                key={method}
                icon={
                  <div className={css(styles.iconWrapper)}>
                    <BenchlingIcon icon={icon} />
                  </div>
                }
                title={<div className={css(styles.cardButtonTitle)}>{text}</div>}
                description={null}
                containerProps={{
                  styles: styles.cardButton,
                  onClick: () => {
                    onOpenStepInitializationView(initializationViewItem);
                    trackSelectedDataFrameCreationMethodEvent(
                      ActionOrigin.ANALYSIS_TOOL_CHOOSE_DATA_FRAME_CREATION_VIEW,
                      analysis.id,
                      dataFrameCreationMethods.length,
                      method
                    );
                  },
                  ...getTestProps(DATA_FRAME_CREATION_METHOD_OPTIONS[method].dataTestAttrs),
                }}
              />
            );
          })}
        </CardGrid>
      </StepConfigurationViewTemplate>
    </div>
  );
}

const styles = StyleSheet.create({
  cardButton: {
    cursor: 'pointer',
    ':hover': {
      background: GRAY_LIGHTER,
    },
  },
  cardButtonTitle: {
    fontSize: 13,
    lineHeight: '26px',
    fontWeight: 'normal',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  iconWrapper: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    width: 26,
    height: 26,
    borderRadius: 4,

    background: GRAY_LIGHT,
  },
});
